import { useQuery } from '@tanstack/react-query'
import { RefreshCcw } from 'lucide-react'

import { fetchAuditLogs, type AuditLog } from '../lib/api'

export function AuditPage() {
  const auditQuery = useQuery({
    queryKey: ['audit-logs'],
    queryFn: () => fetchAuditLogs(50)
  })

  const logs = auditQuery.data ?? []

  return (
    <section className="auditPage">
      <div className="toolbar">
        <div>
          <p className="eyebrow">Governance</p>
          <h1>Audit</h1>
        </div>
        <button
          aria-label="Refresh audit logs"
          className="secondaryButton"
          type="button"
          onClick={() => {
            void auditQuery.refetch()
          }}
          disabled={auditQuery.isFetching}
        >
          <RefreshCcw size={18} aria-hidden="true" />
          <span>{auditQuery.isFetching ? 'Refreshing' : 'Refresh'}</span>
        </button>
      </div>

      <div className="auditSummary">
        <Metric label="Status" value={auditQuery.error?.message ?? 'Ready'} />
        <Metric label="Records" value={String(logs.length)} />
        <Metric label="Window" value="Latest 50" />
      </div>

      <div className="auditTimeline">
        {logs.length > 0 ? (
          logs.map((log) => <AuditCard key={log.id} log={log} />)
        ) : (
          <div className="auditEmpty">
            {auditQuery.isFetching ? 'Loading audit logs' : 'No audit logs'}
          </div>
        )}
      </div>
    </section>
  )
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  )
}

function AuditCard({ log }: { log: AuditLog }) {
  return (
    <article className="auditCard">
      <div className="auditCardHeader">
        <div>
          <span className="compareEyebrow">{formatDateTime(log.created_at)}</span>
          <h2>{formatAction(log.action)}</h2>
        </div>
        <span className="statusPill">{log.target_type}</span>
      </div>

      <dl className="auditDetails">
        <Detail label="Actor" value={log.actor_user_id} />
        <Detail label="Target" value={log.target_id} />
        <Detail label="Log id" value={log.id} />
      </dl>

      <pre>{JSON.stringify(log.metadata, null, 2)}</pre>
    </article>
  )
}

function Detail({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value || '-'}</dd>
    </div>
  )
}

function formatAction(value: string): string {
  return value.replace(/_/g, ' ')
}

function formatDateTime(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return date.toLocaleString()
}
